import React, { useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Password } from "primereact/password";
import "./OubliMotDePasse.scss";
import { Controller, useForm } from "react-hook-form";
import { useState } from "react";
import axios from "axios";
import BtnBleu from "Components/Boutons/BtnBleu/btn-bleu";
import Loader from "Components/Loader/loader";

/**
 * OubliMotDePasse
 *
 * Composant affichant le formulaire de réinitialisation du mot de passe
 *
 * L'utilisateur doit entre son email, puis le code reçu par mail, puis son nouveau mot de passe
 *
 * En cas de succès, l'utilisateur est redirigé vers le modal de connexion.
 * Sinon, un toast d'erreur est affiché.
 *
 * @param {boolean} visible - Affiche ou non le modal OubliMotDePasse
 * @param {Function} setVisible - Fonction permettant de changer la visibilité du modal OubliMotDePasse
 * @param {Object} auth - Objet contenant les propriétés de l'authentification (redux)
 * @returns {ReactElement} Formulaire de réinitialisation du mot de passe
 */
const OubliMotDePasse = (props) => {
  const [error, setError] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSendingMail, setIsSendingMail] = useState(true);
  const [oldValues, setOldValues] = useState(null);

  const defaultValues = {
    email: "",
    resetKey: "",
    password: "",
  };

  useEffect(() => {
    !props.visible && setIsLoggingIn(false);
  }, [props.visible]);
  // variables du formulaire
  const { getValues, handleSubmit, control, reset } = useForm({
    defaultValues,
  });

  const showSuccess = (message) => {
    props.auth.toast.current.show({
      severity: "success",
      summary: "Succès : ",
      detail: message,
      life: 3000,
    });
  };

  const showError = (message) => {
    props.auth.toast.current.show({
      severity: "error",
      summary: "error : ",
      detail: message,
      life: 3000,
    });
  };

  const onSubmit = () => {
    const data = getValues();

    if (isSendingMail && data.email !== "") {
      setIsLoggingIn(true);
      const sendMailData = {
        email: data.email,
      };
      sendMail(sendMailData);
      setError(null);
    } else if (
      !isSendingMail &&
      oldValues &&
      data.resetKey !== "" &&
      data.password !== ""
    ) {
      setIsLoggingIn(true);
      const dataForReset = {
        password: data.password,
      };
      resetPassword(dataForReset, data.resetKey);
      setError(null);
    } else {
      setError("Certaines informations ne sont pas remplies");
    }
  };

  const sendMail = (data) => {
    axios
      .post(`${process.env.REACT_APP_RECRUTEUR_API_URL}/forgot_password/`, data)
      .then(() => {
        axios
          .post(
            `${process.env.REACT_APP_RECRUTEUR_API_URL}/sendTokenEmail`,
            data
          )
          .then((res) => {
            setOldValues(data);
            reset();
            setIsLoggingIn(false);
            setIsSendingMail(false);
            showSuccess(res.data);
          })
          .catch(() => {
            setIsLoggingIn(false);
            showError("Une erreur s'est produite lors de l'envoi du mail");
          });
      })
      .catch(() => {
        setIsLoggingIn(false);
        showError("Une erreur s'est produite lors de l'envoi du mail");
      });
  };

  const resetPassword = (data, resetKey) => {
    axios
      .post(
        `${process.env.REACT_APP_RECRUTEUR_API_URL}/forgot_password/${resetKey}`,
        data
      )
      .then(() => {
        reset();
        setIsLoggingIn(false);
        setIsSendingMail(true);
        showSuccess("Votre mot de passe a bien été modifié");
        props.setVisible(false);
      })
      .catch((error) => {
        setIsLoggingIn(false);
        showError(
          error?.response?.data?.message ||
            "Une erreur s'est produite lors de la réinitialisation de votre mot de passe"
        );
      });
  };

  return (
    <form
      className="forgot__password reset__password"
      onSubmit={handleSubmit(onSubmit)}
    >
      {isSendingMail ? (
        <>
          <div className="forgot__password__field">
            <h4 htmlFor="email">Adresse email</h4>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <>
                  <InputText
                    {...field}
                    placeholder="Adresse email"
                    className="forgot__password__field-email"
                    type="email"
                    id="email"
                    tooltip="Un email avec la clé de réinitialisation de votre mot de passe vous sera envoyé"
                    data-pr-tooltip="Un email avec la clé de réinitialisation de votre mot de passe vous sera envoyé"
                    data-pr-position="bottom"
                    data-pr-classname="forgot__password__tooltip"
                  />
                  <small
                    id="email"
                    className="
                  forgot__password__field-email__small
                  "
                  >
                    Entrez l&apos;adresse email de votre compte
                  </small>
                </>
              )}
            />
          </div>
          {error && <small className="p-error">{error}</small>}
          <div className="forgot__password__button">
            {isLoggingIn ? <Loader /> : <BtnBleu>Envoyer</BtnBleu>}
          </div>
        </>
      ) : (
        <>
          <div className="login__form__field">
            <h4 htmlFor="resetKey">Clé de réinitialisation :</h4>
            <Controller
              name="resetKey"
              control={control}
              render={({ field }) => (
                <Password
                  {...field}
                  feedback={false}
                  autoComplete="new-password"
                  placeholder="Clé de réinitialisation"
                  className="login__form__field-resetKey"
                />
              )}
            />
          </div>
          <div className="login__form__field">
            <h4 htmlFor="password">Nouveau mot de passe</h4>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <Password
                  {...field}
                  autoComplete="new-password"
                  placeholder={"mot de passe"}
                  className={"login__form__field-password"}
                  feedback={false}
                />
              )}
            />
          </div>
          <div
            className="send_new_mail"
            onClick={() => oldValues && sendMail(oldValues)}
          >
            Renvoyer le mail
          </div>
          {error && <small className="p-error">{error}</small>}
          <div className="login__form__button">
            {isLoggingIn ? (
              <Loader />
            ) : (
              <BtnBleu>Changer de mot de passe</BtnBleu>
            )}
          </div>
        </>
      )}
    </form>
  );
};

OubliMotDePasse.propTypes = {
  visible: PropTypes.bool,
  setVisible: PropTypes.func,

  auth: PropTypes.object,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = () => ({});
export default connect(mapStateToProps, mapDispatchToProps)(OubliMotDePasse);
